// @ts-strict-ignore
import React, { useCallback, useContext, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { FormState } from '../types';
import {
  Box, Checkbox, Icon, IconName,
  TextArea,
  Tooltip,
} from '@usealloy/component-library';
import { parseBooleanString } from '~/dashboard/views/workflows/service/util';
import { JQ_TOOLTIP_CONTENT } from './constants';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { updateAction } from '~/dashboard/views/workflows/store/redux/slice';
import { selectDiffMode, selectFocusedAction, selectWorkflowType } from '~/dashboard/views/workflows/store/redux/selectors';
import type { Action } from '~/dashboard/views/workflows/store/engine/types';
import { WORKFLOW_TYPES } from '~/constants';
import { UserContext } from '~/dashboard/context';
import { createWorkflowHistoryThunk } from 'flow/store/redux/thunks';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';
import type { WorkflowStateChange } from 'flow/store/redux/types';
import { Operation } from 'flow/service/changelog/types';
import {
  PLACEHOLDER_DIFF_STATUS, StyledCheckbox, StyledTextAreaWrapper,
} from '../../diffStyles';
import { StyledWorkflowHistoryField } from '../../styles';
import { getNodeEditorFormHtmlElementId } from 'flow/editor/hooks/useWorkflowHistory/helpers';


export default function Footer() {
  const {
    control,
    watch,
    register,
    setValue,
  } = useFormContext<FormState>();

  const dispatch = useAppDispatch();

  const workflowType = useAppSelector(selectWorkflowType);
  const focusedAction = useAppSelector(selectFocusedAction);
  const { featureFlags } = useContext(UserContext);
  const useJq = String(watch('useJq'));
  const jqFunction = watch('jqFunction');
  const { output } = focusedAction;

  const isInDiffMode = useAppSelector(selectDiffMode);

  // TODO: hardcoded until the per-field diff status is wired in. The diff-mode guard is
  // redundant once that lookup returns null outside diff mode, and can be dropped then.
  const diffStatus = isInDiffMode ? PLACEHOLDER_DIFF_STATUS : null;

  // Diff mode is read-only, so the fields below cannot be edited there.
  const isReadOnly = isInDiffMode;


  const commitChange = useCallback(
    (nextOutput: Action['output'], field: keyof FormState) => {
      const stateChange: WorkflowStateChange = () => dispatch(updateAction({
        path: `${focusedAction.publicId}.output`,
        value: nextOutput,
      }));

      dispatch(createWorkflowHistoryThunk(
        stateChange,
        Boolean(featureFlags?.workflowEditorUndoRedo),
        {
          operation: Operation.Modify,
          uiEffectContext: createUiEffectContext(changeDescription.outputJqSettings, {
            actionId: focusedAction.publicId,
            htmlElementId: getNodeEditorFormHtmlElementId('output--rule', field),
          }),
        },
      ));
    },
    [dispatch, featureFlags?.workflowEditorUndoRedo, focusedAction],
  );

  useEffect(() => {
    setValue('useJq', output.useJq);
    setValue('jqFunction', output.jqFunction);
  }, [setValue, output.useJq, output.jqFunction]);

  return workflowType !== WORKFLOW_TYPES.PORTFOLIO && (
    <Box
      alignItems="center"
      direction="column"
      gap={2}
      style={{ padding: 'var(--cl-space-4) var(--cl-space-6)' }}
    >
      <StyledWorkflowHistoryField
        as={Box}
        id={getNodeEditorFormHtmlElementId('output--rule', 'useJq')}
        justifyContent="flex-start"
      >
        <Controller
          control={control}
          name="useJq"
          render={({ field: { onChange, value } }) => (
            <Box justifyContent="flex-start">
              <StyledCheckbox diffStatus={diffStatus}>
                <Checkbox
                  checked={parseBooleanString(value)}
                  label="Parse expression with JQ?"
                  disabled={isReadOnly}
                  onChange={e => {
                    const nextChecked = typeof e === 'boolean'
                      ? e
                      : Boolean(e?.target?.checked);
                    onChange(String(nextChecked));
                    commitChange({
                      ...output,
                      useJq: String(nextChecked),
                      jqFunction,
                    }, 'useJq');
                  }}
                />
              </StyledCheckbox>
              <Tooltip
                side="top"
                content={JQ_TOOLTIP_CONTENT}
              >
                <Icon
                  icon={IconName.Info}
                  size="small"
                />
              </Tooltip>
            </Box>
          )}
        />
      </StyledWorkflowHistoryField>

      {parseBooleanString(useJq) && (
        <StyledWorkflowHistoryField
          as={Box}
          id={getNodeEditorFormHtmlElementId('output--rule', 'jqFunction')}
          style={{ marginTop: 'var(--cl-space-2)' }}
        >
          <StyledTextAreaWrapper diffStatus={diffStatus}>
            <TextArea
              {
              ...register(
                'jqFunction',
                {
                  onBlur: () => commitChange({
                    ...output,
                    useJq,
                    jqFunction,
                  }, 'jqFunction'),
                },
              )
            }
              label="JQ Formula"
              placeholder=".expression |"
              rows={3}
              showLineNumbers
              disabled={isReadOnly}
            />
          </StyledTextAreaWrapper>
        </StyledWorkflowHistoryField>
      )}

    </Box>
  );
}
